import { NetworkStatus } from '@apollo/client';
import { Box, EmptyState, Table, Text } from '@dixa/lib-shared-habla';
import { flexRender, Table as TanstackTable } from '@tanstack/react-table';
import {
  ArticlesViewPlaceholder,
  ArticleTablePlaceholderRow,
} from 'collection/pages/articles/components/articlesTable/placeholders';
import {
  useArticleInfiniteScroll,
  UseArticleInfiniteScrollParams,
} from 'collection/pages/articles/hooks/useArticleInfiniteScroll';
import {
  ArticleListItemResultFragment,
  ArticlesQuery,
  useArticlesQuery,
} from 'lib-knowledge-management-common/graphql';
import { RefObject, useEffect, useRef, useState } from 'react';

import Page from 'lib-knowledge-utils/components/page/page';
import tableStyles from './articlesTable.module.scss';

export type ArticlesTableProps = Pick<
  UseArticleInfiniteScrollParams,
  'lastRowRef'
> &
  Pick<
    ReturnType<typeof useArticlesQuery>,
    'fetchMore' | 'networkStatus' | 'loading'
  > & {
    articlesQuery: ArticlesQuery['articles']['query'] | undefined;
    children?: React.ReactNode;
  };

export function ArticlesTable({
  children,
  articlesQuery,
  loading,
  networkStatus,
  fetchMore,
  lastRowRef,
}: ArticlesTableProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  const hasMore = articlesQuery?.pagination.hasMore || false;
  const rowCount = articlesQuery?.results.length || 0;

  useArticleInfiniteScroll({
    containerRef,
    lastRowRef,
    fetchMore,
    offset: rowCount,
    hasMore,
  });

  useEffect(() => {
    if (networkStatus === NetworkStatus.fetchMore) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' }); // So that the user can see the loading elements beneath previously fetched items
    }
  }, [networkStatus]);

  if (loading && networkStatus !== NetworkStatus.fetchMore)
    return (
      <Page.Section>
        <ArticlesViewPlaceholder />;
      </Page.Section>
    );

  return (
    <Page.Section>
      {rowCount > 0 ? (
        <Table
          headerWithBackground
          framed
          wrapperProps={{
            id: 'tableWrapper',
            ref: containerRef,
          }}
        >
          {children}
        </Table>
      ) : (
        <NoArticlesFound />
      )}
    </Page.Section>
  );
}

type ArticlesTableHeaderProps = {
  table: TanstackTable<ArticleListItemResultFragment>;
};
function ArticlesTableHeader({ table }: ArticlesTableHeaderProps): JSX.Element {
  return (
    <Table.Thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <Table.Tr className={tableStyles.tableRow} key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const isActionColumn = header.column.id === 'id';
            return (
              <Table.Th
                key={header.column.id}
                width={isActionColumn ? '1%' : undefined}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </Table.Th>
            );
          })}
        </Table.Tr>
      ))}
    </Table.Thead>
  );
}
ArticlesTable.Header = ArticlesTableHeader;

export type ArticlesTableBodyProps = {
  table: TanstackTable<ArticleListItemResultFragment>;
  lastRowRef: RefObject<HTMLTableRowElement>;
  networkStatus: NetworkStatus;
};
function ArticlesTableBody({
  table,
  lastRowRef,
  networkStatus,
}: ArticlesTableBodyProps): JSX.Element {
  const rows = table.getRowModel().rows;
  const [initialTableRows, setInitialTableRows] = useState<string[]>([]);

  useEffect(() => {
    if (!initialTableRows.length) {
      const rowIds = rows.map((row) => row.id);
      setInitialTableRows(rowIds);
    }
  }, [initialTableRows, rows]);

  return (
    <Table.Tbody>
      {rows.map((row, rowIndex) => {
        const numberOfRowsFromBottom = 7; // We want the intersection observer to look at the 7th last item in the list, so that when it scrolls into view, the intersection observer invokes an API call to fetch more items
        const isFifthLastRow =
          rowIndex + 1 === rows.length - numberOfRowsFromBottom;

        return (
          <Table.Tr key={row.id} ref={isFifthLastRow ? lastRowRef : undefined}>
            {row.getVisibleCells().map((cell) => {
              const isActionColumn = cell.column.id === 'id';
              return (
                <Table.Td
                  key={cell.column.id}
                  width={isActionColumn ? '1%' : undefined}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Td>
              );
            })}
          </Table.Tr>
        );
      })}
      {networkStatus === NetworkStatus.fetchMore &&
        Array.from(Array(10)).map((_, index) => (
          <ArticleTablePlaceholderRow key={index} />
        ))}
    </Table.Tbody>
  );
}

ArticlesTable.Body = ArticlesTableBody;

/* Placeholder for when no results returned */
function NoArticlesFound() {
  return (
    <Box padding="400">
      <EmptyState
        size="small"
        iconColor={'var(--dixa-typography-color-tertiary)'}
        title="No results found for this search"
        description={
          <Text.Paragraph subdued={true}>
            Please try another search term, check for typos, or adjust your
            filters.
          </Text.Paragraph>
        }
        icon="file-text"
      />
    </Box>
  );
}
